import React from "react";
import { Inbox, ScanLine, Cpu, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { useI18n } from "@/i18n";

export const ORDER_STAGES = [
  { key: "received", label: "Reçu", icon: Inbox },
  { key: "diagnostic", label: "Analyse", icon: ScanLine },
  { key: "tuning", label: "Traitement", icon: Cpu },
  { key: "quality", label: "Contrôle qualité", icon: ShieldCheck },
  { key: "delivered", label: "Livré", icon: CheckCircle2 },
];

// Derive a default step index from the legacy status when no explicit step is set.
export const stepFromStatus = (status) =>
  ({ received: 0, in_progress: 2, completed: 4 }[status] ?? 0);

export const ProgressStepper = ({ step = 0, editable = false, onSet, note, busy = false }) => {
  const { t } = useI18n();
  return (
    <div className="glass rounded-md p-6" data-testid="progress-stepper">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">{t("stepper.title")}</h3>
        {editable && <span className="font-mono text-[9px] uppercase tracking-widest text-aurora-cyan flex items-center gap-1">{busy && <Loader2 className="w-3 h-3 animate-spin" />} {t("stepper.hint")}</span>}
      </div>

      <div className="flex items-start">
        {ORDER_STAGES.map((s, i) => {
          const done = i < step;
          const current = i === step;
          const Icon = s.icon;
          const Wrapper = editable ? "button" : "div";
          return (
            <React.Fragment key={s.key}>
              <Wrapper
                {...(editable ? { onClick: () => onSet?.(i), "data-testid": `set-step-${i}` } : {})}
                className={`flex flex-col items-center gap-2 shrink-0 ${editable ? "cursor-pointer group" : ""}`}
                style={{ flex: "0 0 auto", width: 84 }}
              >
                <div className={`relative w-11 h-11 grid place-items-center rounded-full border-2 transition-all
                  ${done ? "border-signal-success bg-signal-success/15 text-signal-success"
                    : current ? "border-aurora-cyan bg-aurora-cyan/15 text-aurora-cyan shadow-glow animate-pulse"
                    : "border-white/15 bg-white/5 text-zinc-500"} ${editable ? "group-hover:scale-110" : ""}`}>
                  {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`font-mono text-[9px] uppercase tracking-widest text-center leading-tight
                  ${done ? "text-signal-success" : current ? "text-aurora-cyan" : "text-zinc-500"}`}>{t(`stages.${s.key}`)}</span>
              </Wrapper>
              {i < ORDER_STAGES.length - 1 && (
                <div className="flex-1 h-0.5 mt-5 rounded-full transition-colors" style={{ background: i < step ? "#00E5A0" : "rgba(255,255,255,0.10)" }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {note && (
        <div className="mt-5 p-3 rounded-sm bg-aurora-cyan/5 border-l-2 border-aurora-cyan">
          <p className="font-mono text-[10px] uppercase tracking-widest text-aurora-cyan mb-1">{t("stepper.workshopMsg")}</p>
          <p className="text-sm text-zinc-300">{note}</p>
        </div>
      )}
    </div>
  );
};
